// src/pages/nations/NationView.tsx

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import type { Nation } from "@/types/api.types";

interface NationViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nation: Nation | null;
}

export default function NationView({
  open,
  onOpenChange,
  nation,
}: NationViewProps) {
  if (!nation) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle>Nation Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Flag */}
          {nation.flagUrl && (
            <div className="flex justify-center">
              <img
                src={nation.flagUrl}
                alt={nation.name}
                className="w-32 h-20 rounded-lg object-cover border-2"
              />
            </div>
          )}

          {/* Name & Status */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-2xl font-bold">{nation.name}</h3>
              <Badge variant="outline" className="font-mono text-sm">
                {nation.code}
              </Badge>
              {nation.isEliminated ? (
                <Badge variant="destructive" className="bg-red-600">
                  Eliminated
                </Badge>
              ) : (
                <Badge variant="success" className="bg-green-600 text-white">
                  Active
                </Badge>
              )}
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4 border-t pt-4">
            <div>
              <p className="text-sm font-medium text-gray-500">ISO Code</p>
              <code className="text-lg bg-gray-100 px-3 py-1 rounded font-mono">
                {nation.code}
              </code>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Group</p>
              {nation.groupName ? (
                <Badge variant="outline" className="font-mono text-sm">
                  {nation.groupName}
                </Badge>
              ) : (
                <p className="text-sm text-gray-400">Not assigned</p>
              )}
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Total Players</p>
              <p className="text-2xl font-bold text-blue-600">
                {nation._count?.players || 0}
              </p>
            </div>

            {nation.isEliminated && nation.eliminatedAt && (
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Eliminated At
                </p>
                <Badge variant="outline" className="text-red-600">
                  {nation.eliminatedAt.replace(/_/g, " ")}
                </Badge>
              </div>
            )}

            <div>
              <p className="text-sm font-medium text-gray-500">Created</p>
              <p className="text-sm">
                {new Date(nation.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Last Updated</p>
              <p className="text-sm">
                {new Date(nation.updatedAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
          </div>

          {/* Warning if eliminated */}
          {nation.isEliminated && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <strong>⚠️ Nation Eliminated:</strong> All players from this
                nation have been deactivated and cannot be selected in fantasy
                teams.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
